/**
 * Token Parsing Service
 *
 * Parses expression strings into tokens and value segments.
 * Based on LogicAppsUX patterns for handling Logic Apps expression syntax:
 * - @{expression} - Template expressions
 * - @outputs('actionName') - Action outputs
 * - @triggerBody() - Trigger body
 * - @parameters('name') - Workflow parameters
 * - @variables('name') - Workflow variables
 * - @body('actionName') - Action body shorthand
 * - Complex expressions with nested functions and dereferences
 */

import type {
  Token,
  TokenType,
  ValueSegment,
  Expression,
  ExpressionFunction,
  Dereference,
  SchemaObject,
} from '@/types/valueSegment';
import {
  ValueSegmentType,
  TokenType as TokenTypeValues,
  ExpressionType,
  TokenBrandColors,
  generateSegmentId,
} from '@/types/valueSegment';

// =============================================================================
// VALIDATION REGEX PATTERNS (from LogicAppsUX)
// =============================================================================

export const ValidationRegex = {
  /** RFC 3339 datetime format */
  datetime:
    /^(\d{4})-(0[1-9]|1[012])-(0[1-9]|[12]\d|3[01])(?:[\sT])([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d|60))?(\.\d+)?(([Zz])|([+|-]([01]\d|2[0-3])))?$/,
  /** Double/float number */
  double: /^(?:[-+])?([0-9]*(\.[0-9]+([eE](?:[-+])[0-9]+)?)?)$/,
  /** Basic email validation */
  email: /.+@.+/,
  /** Integer number */
  integer: /^(?:[-+])?([0-9]+)$/,
  /** Phone number (international format) */
  phone: /^(\+)?(?:[0-9]{5,15})$/,
  /** URL validation */
  url: /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))?)(?::\d{2,5})?(?:\/\S*)?$/i,
  /** US ZIP code (5 digits) */
  zipcode: /^[0-9]{5}$/,
  /** US ZIP code with extension */
  zipcode4: /^[0-9]{5}(?:-[0-9]{4})$/,
  /** Whitespace characters */
  whiteSpace: /\s/g,
  /** CSV format */
  csv: /^([^,]+)(,([^,]+))*$/,
  /** GUID/UUID format */
  guid: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  /** IPv4 address */
  ipv4: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
} as const;

// =============================================================================
// EXPRESSION HELPERS
// =============================================================================

/**
 * Check if a value is a template expression (starts with @ or contains @{/${)
 * Based on LogicAppsUX isTemplateExpression
 */
export function isTemplateExpression(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string' || value.length < 2) {
    return false;
  }
  return value.startsWith('@') || value.includes('@{') || value.includes('${');
}

/**
 * Check if a value contains any expression markers (supports both @{} and ${} formats)
 */
export function containsExpression(value: string): boolean {
  return value.includes('${') || value.includes('@{') ||
    value.includes('@outputs') || value.includes('@body') ||
    value.includes('@parameters') || value.includes('@variables') || value.includes('@trigger');
}

/**
 * Escape a string literal for use in expressions
 */
export function convertToStringLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

// =============================================================================
// PARSING TYPES
// =============================================================================

export interface ParseResult {
  /** Whether parsing was successful */
  success: boolean;
  /** Parsed value segments */
  segments: ValueSegment[];
  /** Any parsing errors */
  errors: ParseError[];
}

export interface ParseError {
  /** Error message */
  message: string;
  /** Position in the input string */
  position: number;
  /** Length of the error span */
  length: number;
}

// =============================================================================
// TOKEN TYPE MAPPING
// =============================================================================

/** Function names mapped to token types */
const FUNCTION_TO_TOKEN_TYPE: Record<string, { type: TokenType; sourceKey?: string }> = {
  outputs: { type: TokenTypeValues.OUTPUTS, sourceKey: 'outputs' },
  body: { type: TokenTypeValues.OUTPUTS, sourceKey: 'body' },
  triggeroutputs: { type: TokenTypeValues.OUTPUTS, sourceKey: 'outputs' },
  triggerbody: { type: TokenTypeValues.OUTPUTS, sourceKey: 'body' },
  parameters: { type: TokenTypeValues.PARAMETER },
  variables: { type: TokenTypeValues.VARIABLE },
  items: { type: TokenTypeValues.ITEM },
  item: { type: TokenTypeValues.ITEM },
  iterationindexes: { type: TokenTypeValues.ITERATIONINDEX },
  agentparameters: { type: TokenTypeValues.AGENTPARAMETER },
};

// =============================================================================
// EXPRESSION SCANNER (Simplified)
// =============================================================================

class ExpressionScanner {
  private position = 0;
  private readonly expression: string;

  constructor(expression: string) {
    this.expression = expression;
  }

  get currentChar(): string {
    return this.expression[this.position] ?? '';
  }

  get isEnd(): boolean {
    return this.position >= this.expression.length;
  }

  advance(): void {
    this.position++;
  }

  peek(offset = 1): string {
    return this.expression[this.position + offset] ?? '';
  }

  skipWhitespace(): void {
    while (!this.isEnd && /\s/.test(this.currentChar)) {
      this.advance();
    }
  }

  readIdentifier(): string {
    const start = this.position;
    while (!this.isEnd && /[a-zA-Z0-9_]/.test(this.currentChar)) {
      this.advance();
    }
    return this.expression.substring(start, this.position);
  }

  readString(quote: string): string {
    this.advance(); // skip opening quote
    const start = this.position;
    while (!this.isEnd && this.currentChar !== quote) {
      if (this.currentChar === quote && this.peek() === quote) {
        this.advance(); // skip escaped quote
      }
      this.advance();
    }
    const value = this.expression.substring(start, this.position);
    this.advance(); // skip closing quote
    return value;
  }

  readNumber(): string {
    const start = this.position;
    if (this.currentChar === '-' || this.currentChar === '+') {
      this.advance();
    }
    while (!this.isEnd && /[0-9.]/.test(this.currentChar)) {
      this.advance();
    }
    // Handle scientific notation
    if (this.currentChar === 'e' || this.currentChar === 'E') {
      this.advance();
      // After advancing, check for optional sign - use explicit string type to avoid TS narrowing issues
      const signChar: string = this.currentChar;
      if (signChar === '-' || signChar === '+') {
        this.advance();
      }
      while (!this.isEnd && /[0-9]/.test(this.currentChar)) {
        this.advance();
      }
    }
    return this.expression.substring(start, this.position);
  }

  expect(char: string): boolean {
    this.skipWhitespace();
    if (this.currentChar === char) {
      this.advance();
      return true;
    }
    return false;
  }
}

// =============================================================================
// EXPRESSION PARSER
// =============================================================================

/**
 * Parse a function expression recursively
 * Handles: functionName(arg1, arg2)?.['property']['nested']
 */
function parseFunctionExpression(scanner: ExpressionScanner): ExpressionFunction | null {
  scanner.skipWhitespace();

  // Read function name
  const functionName = scanner.readIdentifier();
  if (!functionName) {
    return null;
  }

  scanner.skipWhitespace();

  // Expect opening parenthesis
  if (!scanner.expect('(')) {
    return null;
  }

  // Parse arguments
  const args: Expression[] = [];
  scanner.skipWhitespace();

  if (scanner.currentChar !== ')') {
    do {
      scanner.skipWhitespace();
      const arg = parseExpressionValue(scanner);
      if (arg) {
        args.push(arg);
      }
      scanner.skipWhitespace();
    } while (scanner.expect(','));
  }

  // Expect closing parenthesis
  if (!scanner.expect(')')) {
    return null;
  }

  // Parse dereferences: ?.['property'] or ['property'] or .property
  const dereferences: Dereference[] = [];

  while (!scanner.isEnd) {
    scanner.skipWhitespace();
    const isSafe = scanner.expect('?');

    if (scanner.expect('.')) {
      // Dot notation: .property
      const propName = scanner.readIdentifier();
      if (propName) {
        dereferences.push({
          isSafe,
          expression: {
            type: ExpressionType.StringLiteral,
            startPosition: 0,
            endPosition: propName.length,
            expression: propName,
          },
        });
      }
    } else if (scanner.expect('[')) {
      // Bracket notation: ['property'] or [0]
      scanner.skipWhitespace();
      let propExpr: Expression | null = null;

      if (scanner.currentChar === "'" || scanner.currentChar === '"') {
        const quote = scanner.currentChar;
        const propValue = scanner.readString(quote);
        propExpr = {
          type: ExpressionType.StringLiteral,
          startPosition: 0,
          endPosition: propValue.length,
          expression: propValue,
        };
      } else if (/[0-9-]/.test(scanner.currentChar)) {
        const numValue = scanner.readNumber();
        propExpr = {
          type: ExpressionType.NumberLiteral,
          startPosition: 0,
          endPosition: numValue.length,
          expression: numValue,
        };
      } else {
        // Could be a nested expression
        propExpr = parseExpressionValue(scanner);
      }

      scanner.skipWhitespace();
      scanner.expect(']');

      if (propExpr) {
        dereferences.push({ isSafe, expression: propExpr });
      }
    } else if (isSafe) {
      // Had ? but no . or [ - invalid
      break;
    } else {
      break;
    }
  }

  return {
    type: ExpressionType.Function,
    startPosition: 0,
    endPosition: 0,
    expression: functionName,
    name: functionName,
    arguments: args,
    dereferences,
  };
}

/**
 * Parse a single expression value (string literal, number, or function call)
 */
function parseExpressionValue(scanner: ExpressionScanner): Expression | null {
  scanner.skipWhitespace();

  // String literal
  if (scanner.currentChar === "'" || scanner.currentChar === '"') {
    const quote = scanner.currentChar;
    const value = scanner.readString(quote);
    return {
      type: ExpressionType.StringLiteral,
      startPosition: 0,
      endPosition: value.length,
      expression: value,
    };
  }

  // Number literal
  if (/[0-9-+]/.test(scanner.currentChar)) {
    const value = scanner.readNumber();
    return {
      type: ExpressionType.NumberLiteral,
      startPosition: 0,
      endPosition: value.length,
      expression: value,
    };
  }

  // Boolean or null literal
  const identifier = scanner.readIdentifier();
  if (identifier) {
    const lower = identifier.toLowerCase();
    if (lower === 'true' || lower === 'false') {
      return {
        type: ExpressionType.BooleanLiteral,
        startPosition: 0,
        endPosition: identifier.length,
        expression: identifier,
      };
    }
    if (lower === 'null') {
      return {
        type: ExpressionType.NullLiteral,
        startPosition: 0,
        endPosition: identifier.length,
        expression: identifier,
      };
    }

    // Check if it's a function call
    scanner.skipWhitespace();
    if (scanner.currentChar === '(') {
      // It's a function - reset and parse as function
      // We need to create a new scanner at the right position
      const funcScanner = new ExpressionScanner(identifier + scanner['expression'].substring(scanner['position']));
      const func = parseFunctionExpression(funcScanner);
      if (func) {
        // Advance original scanner past the function
        scanner['position'] += funcScanner['position'] - identifier.length;
        return func;
      }
    }

    // Just an identifier (shouldn't happen in well-formed expressions)
    return {
      type: ExpressionType.StringLiteral,
      startPosition: 0,
      endPosition: identifier.length,
      expression: identifier,
    };
  }

  return null;
}

// =============================================================================
// MAIN PARSING FUNCTIONS
// =============================================================================

/**
 * Parse a string value into value segments
 * Identifies expressions and creates tokens for them
 */
export function parseValueToSegments(value: string): ParseResult {
  const segments: ValueSegment[] = [];
  const errors: ParseError[] = [];

  if (!value) {
    return { success: true, segments: [], errors: [] };
  }

  // If not a template expression, return as literal
  if (!isTemplateExpression(value)) {
    segments.push(createLiteralSegment(value));
    return { success: true, segments, errors };
  }

  // Parse string interpolation: "text @{expr} more text"
  let currentPos = 0;
  let literalStart = 0;

  while (currentPos < value.length) {
    // Look for ${...} JavaScript expressions
    if (value[currentPos] === '$' && value[currentPos + 1] === '{') {
      // Add any preceding literal text
      if (currentPos > literalStart) {
        const literalText = value.substring(literalStart, currentPos);
        if (literalText) {
          segments.push(createLiteralSegment(literalText));
        }
      }

      const endBrace = findMatchingBrace(value, currentPos + 1);
      if (endBrace > 0) {
        const exprContent = value.substring(currentPos + 2, endBrace);
        const fullMatch = value.substring(currentPos, endBrace + 1);

        const token = parseExpressionContentToToken(exprContent, fullMatch);
        if (token) {
          segments.push(createTokenSegment(token, fullMatch));
        } else {
          segments.push(createLiteralSegment(fullMatch));
          errors.push({
            message: `Unable to parse expression: ${exprContent}`,
            position: currentPos,
            length: fullMatch.length,
          });
        }

        currentPos = endBrace + 1;
        literalStart = currentPos;
        continue;
      }
    }

    // Look for @{ or @functionName(
    if (value[currentPos] === '@') {
      // Check for escaped @@
      if (value[currentPos + 1] === '@') {
        currentPos += 2;
        continue;
      }

      // Add any preceding literal text
      if (currentPos > literalStart) {
        const literalText = value.substring(literalStart, currentPos);
        if (literalText) {
          segments.push(createLiteralSegment(literalText));
        }
      }

      // Check for @{...} block
      if (value[currentPos + 1] === '{') {
        const endBrace = findMatchingBrace(value, currentPos + 1);
        if (endBrace > 0) {
          const exprContent = value.substring(currentPos + 2, endBrace);
          const fullMatch = value.substring(currentPos, endBrace + 1);

          const token = parseExpressionContentToToken(exprContent, fullMatch);
          if (token) {
            segments.push(createTokenSegment(token, fullMatch));
          } else {
            segments.push(createLiteralSegment(fullMatch));
            errors.push({
              message: `Unable to parse expression: ${exprContent}`,
              position: currentPos,
              length: fullMatch.length,
            });
          }

          currentPos = endBrace + 1;
          literalStart = currentPos;
          continue;
        }
      }

      // Check for @functionName(...) without braces
      const funcMatch = value.substring(currentPos + 1).match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/);
      if (funcMatch && funcMatch[1]) {
        const funcStart = currentPos + 1;
        const funcName = funcMatch[1];
        const parenStart = funcStart + funcName.length;
        const parenEnd = findMatchingParen(value, parenStart + value.substring(parenStart).indexOf('('));

        if (parenEnd > 0) {
          // Also capture any dereferences after the function
          let exprEnd = parenEnd + 1;
          while (exprEnd < value.length) {
            const currentChar = value[exprEnd];
            const nextChar = value[exprEnd + 1] ?? '';
            if (currentChar === '?' && (nextChar === '[' || nextChar === '.')) {
              exprEnd++;
            } else if (currentChar === '[') {
              const bracketEnd = findMatchingBracket(value, exprEnd);
              if (bracketEnd > 0) {
                exprEnd = bracketEnd + 1;
              } else {
                break;
              }
            } else if (currentChar === '.') {
              exprEnd++;
              while (exprEnd < value.length) {
                const c = value[exprEnd];
                if (c && /[a-zA-Z0-9_]/.test(c)) {
                  exprEnd++;
                } else {
                  break;
                }
              }
            } else {
              break;
            }
          }

          const fullMatch = value.substring(currentPos, exprEnd);
          const exprContent = value.substring(currentPos + 1, exprEnd);

          const token = parseExpressionContentToToken(exprContent, fullMatch);
          if (token) {
            segments.push(createTokenSegment(token, fullMatch));
          } else {
            segments.push(createLiteralSegment(fullMatch));
          }

          currentPos = exprEnd;
          literalStart = currentPos;
          continue;
        }
      }
    }

    currentPos++;
  }

  // Add any remaining literal text
  if (literalStart < value.length) {
    const remaining = value.substring(literalStart);
    if (remaining) {
      segments.push(createLiteralSegment(remaining));
    }
  }

  return {
    success: errors.length === 0,
    segments,
    errors,
  };
}

/**
 * Find the matching closing brace for @{
 */
function findMatchingBrace(str: string, openPos: number): number {
  let depth = 1;
  let pos = openPos + 1;
  let inString = false;
  let stringChar = '';

  while (pos < str.length && depth > 0) {
    const char = str[pos];

    if (inString) {
      if (char === stringChar && str[pos - 1] !== '\\') {
        inString = false;
      }
    } else {
      if (char === "'" || char === '"') {
        inString = true;
        stringChar = char;
      } else if (char === '{') {
        depth++;
      } else if (char === '}') {
        depth--;
      }
    }
    pos++;
  }

  return depth === 0 ? pos - 1 : -1;
}

/**
 * Find the matching closing parenthesis
 */
function findMatchingParen(str: string, openPos: number): number {
  let depth = 1;
  let pos = openPos + 1;
  let inString = false;
  let stringChar = '';

  while (pos < str.length && depth > 0) {
    const char = str[pos];

    if (inString) {
      if (char === stringChar && str[pos - 1] !== '\\') {
        inString = false;
      }
    } else {
      if (char === "'" || char === '"') {
        inString = true;
        stringChar = char;
      } else if (char === '(') {
        depth++;
      } else if (char === ')') {
        depth--;
      }
    }
    pos++;
  }

  return depth === 0 ? pos - 1 : -1;
}

/**
 * Find the matching closing bracket
 */
function findMatchingBracket(str: string, openPos: number): number {
  let depth = 1;
  let pos = openPos + 1;
  let inString = false;
  let stringChar = '';

  while (pos < str.length && depth > 0) {
    const char = str[pos];

    if (inString) {
      if (char === stringChar && str[pos - 1] !== '\\') {
        inString = false;
      }
    } else {
      if (char === "'" || char === '"') {
        inString = true;
        stringChar = char;
      } else if (char === '[') {
        depth++;
      } else if (char === ']') {
        depth--;
      }
    }
    pos++;
  }

  return depth === 0 ? pos - 1 : -1;
}

/**
 * Parse expression content into a Token
 */
function parseExpressionContentToToken(expression: string, fullMatch: string): Token | null {
  const trimmed = expression.trim();
  const scanner = new ExpressionScanner(trimmed);

  const parsed = parseFunctionExpression(scanner);
  if (parsed) {
    return functionExpressionToToken(parsed, fullMatch);
  }

  // If we couldn't parse as a function, create an FX token
  return {
    key: `fx_${generateSegmentId()}`,
    title: trimmed,
    tokenType: TokenTypeValues.FX,
    value: fullMatch,
    brandColor: TokenBrandColors[TokenTypeValues.FX],
    expression: {
      type: ExpressionType.Function,
      startPosition: 0,
      endPosition: trimmed.length,
      expression: trimmed,
    },
  };
}

/**
 * Convert a parsed function expression to a Token
 */
function functionExpressionToToken(func: ExpressionFunction, fullMatch: string): Token {
  const lowerName = func.name.toLowerCase();
  const config = FUNCTION_TO_TOKEN_TYPE[lowerName];

  // Extract string argument if present
  const firstArg = func.arguments[0];
  const argValue = firstArg?.type === ExpressionType.StringLiteral
    ? (firstArg as unknown as { expression: string }).expression
    : '';

  // Extract property path from dereferences
  const properties = func.dereferences.map((d) => {
    if (d.expression.type === ExpressionType.StringLiteral) {
      return (d.expression as unknown as { expression: string }).expression;
    }
    return String((d.expression as unknown as { expression: string }).expression);
  });

  if (!config) {
    // Unknown function - create an FX token
    return {
      key: `fx_${generateSegmentId()}`,
      title: buildExpressionTitle(func),
      tokenType: TokenTypeValues.FX,
      value: fullMatch,
      brandColor: TokenBrandColors[TokenTypeValues.FX],
      expression: {
        type: ExpressionType.Function,
        startPosition: 0,
        endPosition: fullMatch.length,
        expression: func.name,
      },
    };
  }

  // Create token based on type
  switch (config.type) {
    case TokenTypeValues.OUTPUTS:
      return createOutputToken(func.name, argValue, properties, fullMatch);
    case TokenTypeValues.PARAMETER:
      return createParameterToken(argValue, fullMatch);
    case TokenTypeValues.VARIABLE:
      return createVariableToken(argValue, fullMatch);
    case TokenTypeValues.ITEM:
      return createItemToken(argValue, properties, fullMatch);
    case TokenTypeValues.ITERATIONINDEX:
      return createIterationIndexToken(argValue, fullMatch);
    case TokenTypeValues.AGENTPARAMETER:
      return createAgentParameterToken(argValue, fullMatch);
    default:
      return {
        key: `fx_${generateSegmentId()}`,
        title: buildExpressionTitle(func),
        tokenType: TokenTypeValues.FX,
        value: fullMatch,
        brandColor: TokenBrandColors[TokenTypeValues.FX],
      };
  }
}

/**
 * Build a display title for a function expression
 */
function buildExpressionTitle(func: ExpressionFunction): string {
  let title = func.name;
  if (func.arguments.length > 0) {
    const args = func.arguments.map((arg) => {
      if (arg.type === ExpressionType.StringLiteral) {
        return (arg as unknown as { expression: string }).expression;
      }
      return '...';
    });
    title += `(${args.join(', ')})`;
  } else {
    title += '()';
  }
  return title;
}

// =============================================================================
// TOKEN CREATORS
// =============================================================================

function createOutputToken(
  functionName: string,
  actionName: string,
  properties: string[],
  fullMatch: string
): Token {
  const isTrigger = functionName.toLowerCase().startsWith('trigger');
  const source = functionName.toLowerCase().includes('body') ? 'body' : 'outputs';

  const title = isTrigger
    ? properties.length > 0
      ? `Trigger - ${properties.join(' > ')}`
      : source === 'body'
        ? 'Trigger Body'
        : 'Trigger Outputs'
    : properties.length > 0
      ? `${actionName} - ${properties.join(' > ')}`
      : `${actionName} ${source}`;

  const token: Token = {
    key: `output_${actionName || 'trigger'}_${properties.join('_') || source}_${generateSegmentId()}`,
    title,
    name: properties[properties.length - 1] ?? source,
    tokenType: TokenTypeValues.OUTPUTS,
    source,
    value: fullMatch,
    brandColor: TokenBrandColors[TokenTypeValues.OUTPUTS],
  };

  if (!isTrigger && actionName) {
    token.actionName = actionName;
  }

  return token;
}

function createParameterToken(paramName: string, fullMatch: string): Token {
  return {
    key: `param_${paramName}_${generateSegmentId()}`,
    title: paramName,
    name: paramName,
    tokenType: TokenTypeValues.PARAMETER,
    value: fullMatch,
    brandColor: TokenBrandColors[TokenTypeValues.PARAMETER],
  };
}

function createVariableToken(varName: string, fullMatch: string): Token {
  return {
    key: `var_${varName}_${generateSegmentId()}`,
    title: varName,
    name: varName,
    tokenType: TokenTypeValues.VARIABLE,
    value: fullMatch,
    brandColor: TokenBrandColors[TokenTypeValues.VARIABLE],
  };
}

function createItemToken(loopName: string, properties: string[], fullMatch: string): Token {
  const token: Token = {
    key: `item_${loopName || 'current'}_${generateSegmentId()}`,
    title: properties.length > 0 ? `Item - ${properties.join(' > ')}` : 'Current Item',
    name: 'item',
    tokenType: TokenTypeValues.ITEM,
    value: fullMatch,
    brandColor: TokenBrandColors[TokenTypeValues.ITEM],
  };
  if (loopName) {
    token.arrayDetails = { loopSource: loopName };
  }
  return token;
}

function createIterationIndexToken(loopName: string, fullMatch: string): Token {
  const token: Token = {
    key: `index_${loopName}_${generateSegmentId()}`,
    title: `Iteration Index${loopName ? ` (${loopName})` : ''}`,
    name: 'iterationIndex',
    tokenType: TokenTypeValues.ITERATIONINDEX,
    value: fullMatch,
    brandColor: TokenBrandColors[TokenTypeValues.ITERATIONINDEX],
  };
  if (loopName) {
    token.arrayDetails = { loopSource: loopName };
  }
  return token;
}

function createAgentParameterToken(paramName: string, fullMatch: string): Token {
  return {
    key: `agentParam_${paramName}_${generateSegmentId()}`,
    title: paramName,
    name: paramName,
    tokenType: TokenTypeValues.AGENTPARAMETER,
    value: fullMatch,
    brandColor: TokenBrandColors[TokenTypeValues.AGENTPARAMETER],
  };
}

// =============================================================================
// SEGMENT CREATORS
// =============================================================================

function createLiteralSegment(value: string): ValueSegment {
  return {
    id: generateSegmentId(),
    type: ValueSegmentType.LITERAL,
    value,
  };
}

function createTokenSegment(token: Token, value: string): ValueSegment {
  return {
    id: generateSegmentId(),
    type: ValueSegmentType.TOKEN,
    token,
    value,
  };
}

// =============================================================================
// SERIALIZATION FUNCTIONS
// =============================================================================

/**
 * Serialize value segments back to a string
 */
export function serializeSegmentsToValue(segments: ValueSegment[]): string {
  return segments.map((seg) => seg.value).join('');
}

/**
 * Check if segments contain any tokens
 */
export function hasTokens(segments: ValueSegment[]): boolean {
  return segments.some((seg) => seg.type === ValueSegmentType.TOKEN);
}

/**
 * Get all tokens from segments
 */
export function extractTokens(segments: ValueSegment[]): Token[] {
  return segments
    .filter((seg): seg is ValueSegment & { token: Token } =>
      seg.type === ValueSegmentType.TOKEN && !!seg.token
    )
    .map((seg) => seg.token);
}

/**
 * Insert a token at a specific position in segments
 */
export function insertTokenAtPosition(
  segments: ValueSegment[],
  token: Token,
  position: number
): ValueSegment[] {
  // If no segments, just return the token
  if (segments.length === 0) {
    return [createTokenSegment(token, token.value ?? '')];
  }

  const result: ValueSegment[] = [];
  let currentPosition = 0;
  let tokenInserted = false;

  for (const segment of segments) {
    const segmentLength = segment.value.length;

    // Check if token should be inserted within this segment
    if (!tokenInserted && position >= currentPosition && position <= currentPosition + segmentLength) {
      const insertPos = position - currentPosition;

      if (segment.type === ValueSegmentType.LITERAL) {
        // Split the literal segment
        if (insertPos > 0) {
          result.push(createLiteralSegment(segment.value.substring(0, insertPos)));
        }
        result.push(createTokenSegment(token, token.value ?? ''));
        if (insertPos < segmentLength) {
          result.push(createLiteralSegment(segment.value.substring(insertPos)));
        }
        tokenInserted = true;
      } else {
        // Can't split a token, insert after
        result.push(segment);
        result.push(createTokenSegment(token, token.value ?? ''));
        tokenInserted = true;
      }
    } else {
      result.push(segment);
    }

    currentPosition += segmentLength;
  }

  // If we haven't inserted yet, append at the end
  if (!tokenInserted) {
    result.push(createTokenSegment(token, token.value ?? ''));
  }

  return result;
}

/**
 * Create segments from a simple string (all literal)
 */
export function createLiteralSegments(value: string): ValueSegment[] {
  if (!value) return [];
  return [createLiteralSegment(value)];
}

/**
 * Convert a token to its expression string using JavaScript ${...} syntax
 */
export function tokenToExpressionString(token: Token): string {
  if (token.value) {
    return token.value;
  }

  switch (token.tokenType) {
    case TokenTypeValues.OUTPUTS:
      if (token.actionName) {
        return token.source === 'body'
          ? `\${body('${token.actionName}')}`
          : `\${outputs('${token.actionName}')}`;
      }
      return token.source === 'body' ? '${triggerBody()}' : '${triggerOutputs()}';
    case TokenTypeValues.PARAMETER:
      return `\${parameters('${token.name}')}`;
    case TokenTypeValues.VARIABLE:
      return `\${variables('${token.name}')}`;
    case TokenTypeValues.ITEM:
      return '${item()}';
    case TokenTypeValues.ITERATIONINDEX:
      return `\${iterationIndexes('${token.arrayDetails?.loopSource ?? ''}')}`;
    case TokenTypeValues.AGENTPARAMETER:
      return `\${agentParameters('${token.name}')}`;
    case TokenTypeValues.FX:
      return `\${${token.title}}`;
    default:
      return token.title;
  }
}

// =============================================================================
// IMPLICIT FOREACH HANDLING (GetValueSegmentHandler pattern from LogicAppsUX)
// =============================================================================

/**
 * Result of checking if a token requires implicit foreach wrapping
 */
export interface ImplicitForeachResult {
  /** Whether an implicit foreach is required */
  requiresForeach: boolean;
  /** The expression to iterate over */
  foreachExpression?: string;
  /** The action name to use for the foreach loop */
  loopActionName?: string;
  /** The wrapped item() expression to use instead */
  wrappedExpression?: string;
}

/**
 * Check if using a token requires creating an implicit ForEach loop
 * This happens when:
 * 1. The token references an array type
 * 2. The target parameter expects a singular value
 * 3. We're not already inside a loop iterating over that array
 *
 * Based on LogicAppsUX GetValueSegmentHandler pattern
 */
export function checkImplicitForeachRequired(
  token: Token,
  targetSchema?: SchemaObject,
  currentLoopContext?: string[]
): ImplicitForeachResult {
  // Only outputs can require implicit foreach
  if (token.tokenType !== TokenTypeValues.OUTPUTS) {
    return { requiresForeach: false };
  }

  // Check if token represents an array
  const isArrayToken = token.type === 'array' || token.schema?.type === 'array';
  if (!isArrayToken) {
    return { requiresForeach: false };
  }

  // Check if target expects an array (in which case, no foreach needed)
  if (targetSchema?.type === 'array') {
    return { requiresForeach: false };
  }

  // Check if we're already in a loop iterating over this array
  const tokenArraySource = token.actionName ?? 'trigger';
  if (currentLoopContext?.includes(tokenArraySource)) {
    return { requiresForeach: false };
  }

  // Generate a loop name based on the action name
  const loopActionName = `ForEach_${tokenArraySource}`;

  return {
    requiresForeach: true,
    foreachExpression: token.value ?? tokenToExpressionString(token),
    loopActionName,
    wrappedExpression: '${item()}',
  };
}

/**
 * Wrap a token expression for use inside an implicit foreach
 * Converts array property access to item() access
 *
 * Example:
 * "${body('GetUsers')?.['users']}" with property "name" becomes "${item()?.['name']}"
 */
export function wrapTokenForImplicitForeach(
  _token: Token,
  propertyPath?: string
): string {
  // The token is used for future enhancements like preserving specific property paths
  if (propertyPath) {
    return `\${item()${propertyPath}}`;
  }
  return '${item()}';
}

/**
 * Generate the foreach expression for wrapping an array token
 */
export function generateForeachExpression(token: Token): string {
  return token.value ?? tokenToExpressionString(token);
}

// =============================================================================
// FORMAT VALIDATION
// =============================================================================

/**
 * Validate a value against a format
 */
export function validateFormat(value: string, format: string): boolean {
  const regex = ValidationRegex[format as keyof typeof ValidationRegex];
  if (regex) {
    return regex.test(value);
  }

  // Additional format checks
  switch (format.toLowerCase()) {
    case 'date':
    case 'date-time':
    case 'datetime':
      return ValidationRegex.datetime.test(value) || !isNaN(Date.parse(value));
    case 'uri':
    case 'url':
      return ValidationRegex.url.test(value);
    case 'email':
      return ValidationRegex.email.test(value);
    case 'int32':
    case 'int64':
    case 'integer':
      return ValidationRegex.integer.test(value);
    case 'float':
    case 'double':
    case 'number':
      return ValidationRegex.double.test(value);
    case 'uuid':
    case 'guid':
      return ValidationRegex.guid.test(value);
    default:
      return true; // Unknown format - allow
  }
}
